import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  fromPromise,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Auth } from 'aws-amplify';
// import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { createClient } from 'graphql-ws';
import { logError } from './error-logger';

const API_URL = process.env.REACT_APP_GRAPHQL_URL;
const REST_URL = process.env.REACT_APP_API_URL;

const errorLink = onError(({ graphQLErrors, networkError, operation, forward, response }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      console.log(
        `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`
      );
      logError(err?.message, {
        module: 'GraphQL',
        customMessage: `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`,
      });
      if (err.message.includes('JWTExpired')) {
        console.log('session expired');
        return fromPromise(
          Auth.currentSession()
            .then((session) => {
              // store the new tokens for your auth link
              console.log('session refreshed');
              return session.getIdToken().getJwtToken();
            })
            .catch((error) => {
              // handle token refresh errors and redirect to login
              console.log(`Auth API error: ${error}`);
              window.location.reload();
            })
        )
          .filter((value) => Boolean(value))
          .flatMap((token) => {
            console.log('set new token');
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                Authorization: `Bearer ${token}`,
              },
            });

            // retry the request, returning the new observable
            return forward(operation);
          });
      }
    }
  }
  if (networkError) {
    logError(networkError?.message, {
      module: 'GraphQL Network',
      customMessage: `[GraphQL Network Error]: Message: ${networkError?.message}`,
    });
    // TODO handle network error Socket closed with event 4400 {"server_error_msg":"4400: Connection initialization failed: Missing 'Authorization' or 'Cookie' header in JWT authenticati
    console.error(`[Network error]:`);
    console.log(networkError);
  }
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: API_URL.replace('https', 'wss'),
    shouldRetry: (error) => {
      return true;
    },
    keepAlive: 10000,
    retryAttempts: Infinity,
    connectionParams: async () => {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const selectedOrganization = localStorage?.getItem('organization-context');
        return {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(selectedOrganization ? { 'x-selected-organization': selectedOrganization } : {}),
          },
        };
      } catch (e) {
        console.error('Connection params error: ', e.message);
      }
    },
  })
);

const httpLink = new HttpLink({
  uri: API_URL,
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const authLink = setContext(async (_, { headers }) => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  const selectedOrganization = localStorage?.getItem('organization-context');
  const hasExplicitOrgHeader =
    headers != null && Object.prototype.hasOwnProperty.call(headers, 'x-selected-organization');

  let organizationHeader = {};
  if (hasExplicitOrgHeader) {
    if (headers['x-selected-organization']) {
      organizationHeader = { 'x-selected-organization': headers['x-selected-organization'] };
    }
  } else if (selectedOrganization) {
    organizationHeader = { 'x-selected-organization': selectedOrganization };
  }

  const { 'x-selected-organization': _explicitOrg, ...restHeaders } = headers || {};

  return {
    headers: {
      ...restHeaders,
      Authorization: `Bearer ${token}`,
      ...organizationHeader,
    },
  };
});

const customFetchWithRetry = async (uri, options) => {
  const attemptFetch = async (retryCount = 1) => {
    const separator = uri.includes('?') ? '&' : '?';
    const urlWithTimestamp = `${uri}${separator}_ts=${Date.now()}`;
    try {
      const response = await fetch(urlWithTimestamp, {
        ...options,
      });

      if (response?.status !== 504) {
        return response;
      }

      if (retryCount < 2) {
        console.warn(`Received 504 Gateway Timeout. Retrying... (Attempt ${retryCount + 1})`);
        return attemptFetch(retryCount + 1);
      }

      return response;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };

  return attemptFetch();
};
const restLink = new RestLink({
  uri: REST_URL,
  customFetch: customFetchWithRetry,
});

const client = new ApolloClient({
  cache: new InMemoryCache({
    dataIdFromObject: (responseObject) => {
      const { __typename, id, ...rest } = responseObject;
      switch (__typename) {
        default: {
          return defaultDataIdFromObject(responseObject);
        }
      }
    },
  }),
  link: ApolloLink.from([errorLink, authLink, restLink, link]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
export default client;
